import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  IconButton,
  InputAdornment,
  Alert,
} from '@mui/material';
import { Visibility, VisibilityOff, Info as InfoIcon } from '@mui/icons-material';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { CompanyRegistrationData } from '../../types/company';

interface ContactInfoStepProps {
  register: UseFormRegister<CompanyRegistrationData>;
  errors: FieldErrors<CompanyRegistrationData>;
  watchedValues: Partial<CompanyRegistrationData>;
  onFieldFocus: (fieldName: keyof CompanyRegistrationData) => void;
}

export const ContactInfoStep: React.FC<ContactInfoStepProps> = ({
  register,
  errors,
  watchedValues,
  onFieldFocus,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
        代表者情報
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        企業管理者アカウントの代表者情報を入力してください。
      </Typography>

      {/* 代表者メールアドレスの注意書き */}
      <Alert 
        severity="info" 
        icon={<InfoIcon />}
        sx={{ 
          mb: 3,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          重要：代表者メールアドレスについて
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            このアカウントは<strong>企業管理者</strong>として登録されます
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            必ず<strong>代表者ご本人のメールアドレス</strong>で登録してください
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            <strong>人事担当者</strong>や<strong>従業員</strong>のログインアカウントは後から追加可能です
          </Typography>
          <Typography component="li" variant="body2">
            ご不明な点がございましたらサポートまでお問い合わせください
          </Typography>
        </Box>
      </Alert>

      <Grid container spacing={3}>
        {/* 代表者メールアドレス */}
        <Grid item xs={12}>
          <TextField
            {...register('contactEmail')}
            label="代表者メールアドレス"
            type="email"
            fullWidth
            required
            placeholder="例: ceo@company.co.jp"
            error={!!errors.contactEmail}
            helperText={
              errors.contactEmail?.message ||
              '※ このメールアドレスがログインIDになります'
            }
            onFocus={() => onFieldFocus('contactEmail')}
            inputProps={{ 'data-testid': 'contact-email' }}
          />
        </Grid>

        {/* パスワード */}
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('password')}
            label="パスワード"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            required
            placeholder="8文字以上の英数字"
            error={!!errors.password}
            helperText={
              errors.password?.message ||
              '※ 英数字を含む8文字以上で設定してください'
            }
            onFocus={() => onFieldFocus('password')}
            inputProps={{ 'data-testid': 'password' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* パスワード確認 */}
        <Grid item xs={12} sm={6}>
          <TextField
            {...register('confirmPassword')}
            label="パスワード確認"
            type={showConfirmPassword ? 'text' : 'password'}
            fullWidth
            required
            placeholder="上記と同じパスワードを入力"
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            onFocus={() => onFieldFocus('confirmPassword')}
            inputProps={{ 'data-testid': 'confirm-password' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={handleToggleConfirmPasswordVisibility}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
